<template>
  <div class="map-page" :class="{ 'mobile-testbed': mobileTestbedActive }">
    <photo-viewer />
    <search-this-area v-if="showSTA" />
    <main>
      <!-- Loader -->
      <x-loading-bar
        :duration="10"
        :completed="progressBarLoaded"
        :time-to-hide="1.2"
      />

      <!-- Nav -->
      <div class="nav-scrim" />
      <side-nav :show-referral="false">
        <template #nav-links>
          <nav-links />
        </template>
      </side-nav>

      <!-- Panels -->
      <router-view v-slot="{ Component }" class="side-panel panel-view">
        <animation-wrap :type="ANIMATION_TYPES.FADE_TRANSLATE">
          <component :is="Component" />
        </animation-wrap>
      </router-view>
      <map-viewer
        v-if="mapManagerReady"
        :features="mapFeatures"
        :draw-manager="drawManager"
        :shown-layers="mapShownLayers"
        :layer-opacities="mapLayerOpacities"
        :layer-filters="mapLayerFilters"
        :stylesheet-sources="mapStylesheetSource"
        :data-markups-loaded="areMarkupsLoaded"
        @map-click="mapClickHandler"
        @map-event="mapEventHandler"
        @fps-measured="handleFpsMeasurement"
      />

      <!-- Controls -->
      <div
        :class="[
          'controls-top',
          { 'mobile-menu-open': mapControlState?.mobileMenuOpen },
        ]"
      >
        <div v-show="mapControlState?.search">
          <fish-search v-if="searchPackageActive" />
          <control-search v-else />
        </div>
      </div>
      <animation-wrap :type="ANIMATION_TYPES.FADE_TRANSLATE">
        <div v-if="showFilterButton" class="quick-filters-container">
          <quick-filters />
        </div>
      </animation-wrap>

      <control-crosshair v-show="mapControlState?.crosshair" />
      <control-weather-crosshair v-show="mapControlState?.weatherCrosshair" />
      <div
        class="controls-inner-right controls-column-right controls-inner-right--align-top"
      >
        <div class="controls-spacer" />
        <ys-button
          v-if="isBasicUser"
          variant="primary"
          size="small"
          data-test="upgrade-button"
          @click="goToUpgrade"
        >
          Upgrade
        </ys-button>
      </div>
      <div
        class="controls-outside-right controls-column-right controls-outside-right--align-top"
      >
        <control-toolbox v-show="mapControlState?.toolbox" />
        <div class="controls-spacer" />
        <control-compass
          v-show="mapControlState?.compass"
          :disabled="isDisabled(mapControlState?.compass)"
        />
        <control-weather
          v-show="mapControlState?.weather"
          :disabled="isDisabled(mapControlState?.weather)"
        />
        <control-dimension v-show="mapControlState?.dimensions" />
        <control-zoom
          v-show="mapControlState?.zoom"
          :disabled="isDisabled(mapControlState?.zoom)"
        />
        <control-layers :disabled="isDisabled(mapControlState?.dimensions)" />
        <control-user-location v-show="mapControlState?.userLocation" />
      </div>
      <div class="controls-bottom-right">
        <control-scale v-if="mapManagerReady" v-show="mapControlState?.scale" />
        <control-cursor-location v-show="mapControlState?.cursorLocation" />
      </div>
      <more-info-card />

      <div class="controls-bottom-center controls-column-center">
        <animation-wrap :type="ANIMATION_TYPES.FADE_TRANSLATE">
          <control-recent-imagery v-if="highFreqActive" />
        </animation-wrap>
        <animation-wrap :type="ANIMATION_TYPES.FADE_TRANSLATE">
          <control-wind v-if="windLayerEnabled" />
        </animation-wrap>
      </div>
      <div class="controls-bottom-left">
        <control-attribution
          v-if="mapManagerReady && activeViewer === 'Mapbox'"
        />
      </div>
      <control-ai-chat
        v-if="isActive('ai_guide_chat')"
        :dependencies="aiChatDependencies()"
      />
    </main>
  </div>
</template>

<script setup lang="ts">
///// imports /////
import {
  inject,
  ref,
  computed,
  onBeforeMount,
  onMounted,
  onUnmounted,
  watch,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { provideApolloClient } from '@vue/apollo-composable'
import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import '@onxmaps/yellowstone-ui/ys-button'

// Components
import sideNav from '@/components/side-nav/index.vue'
import SmartBanner from '@/components/smart-banner'
import navLinks from '@/fish/components/nav/nav-links.vue'
import FishCollection from '@/fish/components/layers/fish-collection'
import quickFilters from '@/fish/components/filtering/quick-filters.vue'

import Account from '@/components/account'
import moreInfoCard from '@/components/more-info-card.vue'
// eslint-disable-next-line no-restricted-imports
import mapViewer from '@/components/map-viewer/map-viewer.vue'
import photoViewer from '@/components/photo-viewer.vue'
import xLoadingBar from '@/components/shared/x-loading-bar/index.vue'
import animationWrap from '@/components/shared/animation-wrap.vue'
import searchThisArea from '@/components/search/search-this-area.vue'
import controlDimension from '@/fish/components/map-controls/control-dimension.vue'
import fishSearch from '@/fish/components/search/fish-search.vue'
import controlWind from '@/fish/components/ui/wind/wind-controller.vue'

// Libs
import split from '@/lib/split'
import { clickHandler as mapClickHandler } from '@/lib/map-click-handler'
import { eventHandler as mapEventHandler } from '@/lib/map-event-handler'
import { useMeasureFps } from '@/composables/fps-monitor'
import drawManager from '@/lib/mapbox/draw-manager-plugin'
import {
  ANIMATION_TYPES,
  OFFLINE_MAPS_SOURCE,
  HIGHLIGHT_SOURCE,
  QUERY_MARKER_SOURCE,
  ELEVATION_POINT_SOURCE,
  OFFLINE_OVERLAY_SOURCE,
  WAYPOINTS_SOURCE,
  WAYPOINTS_LABEL_SOURCE,
  HIGHLIGHTED_WAYPOINTS_SOURCE,
  LINES_SOURCE,
  TRACKS_SOURCE,
  SHAPES_SOURCE,
  ROUTES_SOURCE,
  SEARCH_PIN_SOURCE,
} from '@/lib/enums'
import { isMobileTestbedActive } from '@/lib/prototyping-flags'
import browser from '@/lib/browser'
import { SLUGS, FIELDS } from '@/fish/lib/taxonomy-constants'
import { isSearchPackageActive } from '@/lib/search/search-utils'
import { HUMAN_SKUS } from '@/lib/plans'
import { goToPurchase } from '@/lib/purchase-utils'
import { PURCHASE_CTA_ORIGINS } from '@/lib/enums-marketing'
// Services
import { controlState } from '@/services/control-layout-service'
import {
  initializeBasemap,
  ensureDefaultBasemap,
  isHighFreqActive,
  basemap,
  Basemap,
  basemapMarketingName,
} from '@/services/basemap-service'
import {
  initializeLMS,
  shownLayers,
  layerOpacities,
  layerFilters,
  stylesheetSources,
  hideLayers,
  showLayers,
} from '@/services/layer-management-service'
import markupService, { markupsLoaded } from '@/services/markup-service'
import markupRenderService from '@/services/markup-service/render'
import contentCollectionService from '@/services/content-collection-service'
import layerService from '@/services/layer-service'
import { redirectAfterUpsell } from '@/services/upsell-redirect-service'
import {
  featuresHighlighted,
  queryMarker,
  elevationScrubMarker,
  searchPinFeatures,
  mapReadyPromise,
  camera,
  getIsIdle,
  setUserCoord,
  geolocationEnabled,
  viewerDimension,
} from '@/services/map-service'

import { isActive } from '@/services/feature-flag-service'
import { showSearchArea, setShowSearchArea } from '@/services/search-service'
import waypointImageService from '@/services/waypoint-image-service'
import { offlineServiceState } from '@/services/offline-map-service'
import { initializeXOfflineMapsWhenEnabled } from '@/services/offline-map-service/init'
import {
  getTotalFilterLength,
  checkAndSetFilterPreferences,
  hasSliderFilter,
} from '@/services/filter-layer-service'
import subscriptionService from '@/services/subscription-service'
import marketingEventService from '@/services/marketing-event-service'
import type { InjectedApolloObject } from '@/services/apollo-provider'
import { FILTER_KEYS } from '@/services/filter-layer-service/constants'
import {
  getIsMapManagerReady,
  getActiveViewer,
  getIsMapViewerReady,
  getMapManager,
} from '@/services/map-provider/map-manager-state'
import { useHasAccess } from '@/services/feature-access/composables'
import { FishGates } from '@/services/feature-access/fish/types'

// control imports
import controlAttribution from '@/components/map-controls/control-attribution.vue'
import controlScale from '@/components/map-controls/control-scale.vue'
import controlCompass from '@/components/map-controls/control-compass.vue'
import controlZoom from '@/components/map-controls/control-zoom.vue'
import controlUserLocation from '@/components/map-controls/control-user-location.vue'
import controlSearch from '@/components/search/control-search.vue'
import controlCrosshair from '@/components/map-controls/control-crosshair.vue'
import controlCursorLocation from '@/components/map-controls/control-cursor-location.vue'
import controlWeather from '@/components/map-controls/control-weather.vue'
import controlToolbox from '@/components/map-controls/control-toolbox.vue'
import controlWeatherCrosshair from '@/components/map-controls/control-weather-crosshair.vue'
import controlRecentImagery from '@/components/map-controls/control-recent-imagery.vue'
import controlAiChat from '@/lib/ai/components/control-ai-chat.vue'
import { aiDependencies } from '@/fish/lib/ai'
import type { AiDependencies } from '@/lib/ai'
import controlLayers from '@/fish/components/map-controls/control-layers/index.vue'
import { getModeToggleableLayers } from '@/components/map-controls/control-layers/utils.js'
import { getItem, getItemRef, saveItem } from '@/plugins/storage'
import { setWindLayer } from '@/fish/components/ui/wind/utils'
import { WIND_LAYER_INFO } from '@/fish/components/map-controls/utils'

import { applyMapFilteringDisplay } from '@/fish/lib/filtering/filter-map-display'
import {
  determineSpeciesToFilter,
  getSlugListToFilter,
} from '@/fish/lib/filtering/filter-builders'
import highlightFavoritesOnMap from '@/fish/lib/highlight-favorites'
import { getAllFavoritedWaterbodies } from '@/fish/lib/highlight-favorites/operations'

import { Vertical } from '@/types/verticals'
import { FishMode } from '@/types/modes'

import useSearchThisArea from '@/composables/search-this-area'

import {
  provideXWeather,
  type XWeatherCredentials,
} from '@/fish/providers/x-weather'

///// refs and variables /////
const { getDataOnDiscoverSearchThisArea } = useSearchThisArea()
getDataOnDiscoverSearchThisArea(async () => await applyMapFilteringDisplay())

const xWeatherCredentials: XWeatherCredentials = {
  clientId: window.environment.xWeatherIdFish,
  clientSecret: window.environment.xWeatherSecretFish,
}

const route = useRoute()
const router = useRouter()
const xWeather = provideXWeather(xWeatherCredentials)
const { hasAccess: hasWindAccess } = useHasAccess(FishGates.animated_wind)

const apolloClient = inject<InjectedApolloObject>('apollo')
if (apolloClient && apolloClient.client) {
  provideApolloClient(apolloClient.client)
}

const { handleFpsMeasurement } = useMeasureFps()

// Built on first use and memoized, rather than at setup: with `ai_guide_chat`
// off nobody should pay to construct the Guide's adapters, and the template's
// `v-if` on the flag is what keeps this from ever being called in that case.
//
// The memo isn't an optimization -- `adapters.map.getLayer`
// (control-ai-chat.vue) reads through the layers adapter on every tool-result
// render, so the identity has to stay stable across re-renders.
//
// Deliberately a plain memo and not a `computed`: `createLayersAdapter()` reads
// feature-flag state, which a computed would track as a reactive dependency and
// then mint a replacement adapter if it ever changed.
let memoizedAiChatDependencies: AiDependencies | undefined
function aiChatDependencies(): AiDependencies {
  memoizedAiChatDependencies ??= aiDependencies({
    wind: { xWeather, hasAccess: () => hasWindAccess.value },
  })
  return memoizedAiChatDependencies
}

const initialMapLoad = ref(true)
const progressBarLoaded = ref(false)
const areMarkupsLoaded = ref(false)
const showFilterButton = ref(false)
let intervalId: ReturnType<typeof setInterval> | undefined
const windLayerEnabled = getItemRef<boolean>('wind-forecast-enabled')
const fishLayersRef =
  getItemRef<Array<{ id: string; active: boolean }>>('fishLayers')

///// computed /////
const mapViewerReady = computed(() => getIsMapViewerReady())
const activeViewer = computed(() => getActiveViewer())
const mobileTestbedActive = computed(() => isMobileTestbedActive())
const searchPackageActive = computed(() => isSearchPackageActive())
const highFreqActive = computed(isHighFreqActive)
const mapManagerReady = computed(() => {
  return getIsMapManagerReady()
})
const mapControlState = computed(controlState)
const mapStylesheetSource = computed(stylesheetSources)
const showSTA = computed(showSearchArea)
const mapShownLayers = computed(shownLayers)
const mapLayerOpacities = computed(layerOpacities)
const mapLayerFilters = computed(layerFilters)
const mapFeatures = computed(() => {
  return {
    [WAYPOINTS_SOURCE]: {
      handler:
        markupRenderService.getWaypointsGeoJson.bind(markupRenderService),
      trigger: () => markupRenderService.waypointsNeedsUpdate,
    },
    [WAYPOINTS_LABEL_SOURCE]: {
      handler:
        markupRenderService.getWaypointLabelsGeoJson.bind(markupRenderService),
      trigger: () => markupRenderService.waypointsNeedsUpdate,
    },
    [HIGHLIGHTED_WAYPOINTS_SOURCE]: {
      handler:
        markupRenderService.getHighlightedWaypointsGeoJson.bind(
          markupRenderService
        ),
      trigger: () => markupRenderService.waypointsNeedsUpdate,
      callback: () => (markupRenderService.waypointsNeedsUpdate = false),
    },
    [LINES_SOURCE]: {
      handler: markupRenderService.getLinesGeoJson.bind(markupRenderService),
      trigger: () => markupRenderService.linesNeedsUpdate,
      callback: () => (markupRenderService.linesNeedsUpdate = false),
    },
    [TRACKS_SOURCE]: {
      handler: markupRenderService.getTracksGeoJson.bind(markupRenderService),
      trigger: () => markupRenderService.tracksNeedsUpdate,
      callback: () => (markupRenderService.tracksNeedsUpdate = false),
    },
    [SHAPES_SOURCE]: {
      handler: markupRenderService.getShapesGeoJson.bind(markupRenderService),
      trigger: () => markupRenderService.shapesNeedsUpdate,
      callback: () => (markupRenderService.shapesNeedsUpdate = false),
    },
    [ROUTES_SOURCE]: {
      handler: markupRenderService.getRoutesGeoJson.bind(markupRenderService),
      trigger: () => markupRenderService.routesNeedsUpdate,
      callback: () => (markupRenderService.routesNeedsUpdate = false),
    },
    [OFFLINE_MAPS_SOURCE]: {
      handler: () => offlineServiceState.maps,
    },
    [HIGHLIGHT_SOURCE]: {
      handler: featuresHighlighted,
    },
    [QUERY_MARKER_SOURCE]: {
      handler: queryMarker,
    },
    [ELEVATION_POINT_SOURCE]: {
      handler: elevationScrubMarker,
    },
    [OFFLINE_OVERLAY_SOURCE]: {
      handler: () => offlineServiceState.overlayFeature,
    },
    [SEARCH_PIN_SOURCE]: {
      handler: searchPinFeatures,
    },
  }
})

const mapCamera = computed(camera)

const hasAppliedFilters = computed(() => getTotalFilterLength() > 0)

const isBasicUser = computed(() => subscriptionService.isBasic())

///// functions /////
const goToUpgrade = () => {
  const sku = HUMAN_SKUS.FISH_ELITE_YEAR
  goToPurchase(
    {
      origin: PURCHASE_CTA_ORIGINS.MAP_SCREEN,
      detail: 'Upgrade',
    },
    `product=${sku}&membership=elite`
  )
}

const isDisabled = (stateValue: string | boolean) => {
  return stateValue === 'disabled'
}

const awaitMap = async () => {
  await mapReadyPromise

  const upsellRedirect = redirectAfterUpsell()
  if (upsellRedirect) {
    router.push(upsellRedirect)
  }

  // set loading bar to finish
  progressBarLoaded.value = true
}

const initialPromises = async () => {
  await Promise.allSettled([initializeLMS(), initializeBasemap()])
}

const setCollectionsData = async () => {
  const collectionData = await layerService.requestCollections()
  new FishCollection(collectionData)
  layerService.layersReadyResolver?.()
}

// Gather metrics about current state of user data when the map loads
const fireSessionStart = () => {
  interface Layer {
    id: string
    label: string
    byline: string
    component: string
    img: string
    fullImg: string
    active: boolean
    displayActive: boolean
    hasAccess: boolean
  }

  const windEnabled = getItem('wind-forecast-enabled')
  const layersArr = getModeToggleableLayers(FishMode.Freshwater, Vertical.Fish)
    .filter((layer: Layer) => layer.displayActive)
    .map((layer: Layer) => layer.label)
  windEnabled && layersArr.push(WIND_LAYER_INFO.label)

  const layers = layersArr.join()

  const species = determineSpeciesToFilter().join()
  const launchApplied = getSlugListToFilter(SLUGS.LAUNCH).join()
  const surfaceApplied = getSlugListToFilter(SLUGS.RAMP).join()
  const amenitiesApplied = getSlugListToFilter(FIELDS.AMENITIES).join()
  const areaApplied = hasSliderFilter(FILTER_KEYS[FIELDS.AREA])

  marketingEventService.publish('fish_user_state_loaded', {
    // Map State
    lat_long: `${mapCamera.value?.location.latitude},${mapCamera.value?.location.longitude}`,
    active_basemap: basemapMarketingName.value,
    map_viewer_dimension: viewerDimension(),
    active_layers: layers,
    // Filter State
    species_filter_applied: species,
    trophy_filter_applied: getItem('fish_freshwater_filter_trophy'),
    abundance_filter_applied: getItem('fish_freshwater_filter_abundance'),
    keeper_filter_applied: getItem('fish_freshwater_filter_keeper'),
    high_stocking_effort_filter_applied: getItem(
      'fish_freshwater_filter_stocking_effort'
    ),
    boat_launch_applied: launchApplied,
    boat_launch_surface_applied: surfaceApplied,
    amenities_applied: amenitiesApplied,
    lake_area_applied: !!areaApplied,
    // User State
    membership_level: subscriptionService.getMembershipLevel(),
  })
}

///// watchers /////
watch(route, (to) => {
  showFilterButton.value = !to?.path.includes('waterbodies')
})

watch(
  fishLayersRef,
  (newVal, oldVal) => {
    const areAreasActive =
      newVal?.find((layer) => layer.id === 'fishing_areas')?.active === true
    const wereAreasActive =
      oldVal?.find((layer) => layer.id === 'fishing_areas')?.active === true
    if (areAreasActive && !wereAreasActive && hasAppliedFilters.value) {
      applyMapFilteringDisplay()
    }
  },
  { deep: true }
)

watch(
  mapCamera,
  debounce((newCamera, oldCamera) => {
    // Show "Search this Area" if
    if (
      // User has filters applied
      hasAppliedFilters.value &&
      // On first watch oldCam is null which represents hard refresh
      oldCamera !== null &&
      // Did map move
      !isEqual(newCamera, oldCamera)
    ) {
      setShowSearchArea(true)
    } else {
      setShowSearchArea(false)
    }
  }, 100)
)

watch(getIsIdle, async () => {
  if (initialMapLoad.value) {
    if (hasAppliedFilters.value) {
      await applyMapFilteringDisplay()
    }
    initialMapLoad.value = false
  }
})

// Update user location 1/s if enabled
watch(geolocationEnabled, () => {
  if (geolocationEnabled()) {
    intervalId = setInterval(async () => {
      await setUserCoord()
    }, 1000)
  }
})

// Watch basemap changes and react to employee flag to toggle "supported area" mask
watch(
  basemap,
  (newBasemap) => {
    let pattern = null
    if (isActive('remove_mask')) {
      if (newBasemap === Basemap.TOPO) {
        pattern = 'dark'
      } else {
        pattern = 'light'
      }
      hideLayers([
        `fish_mask$pattern_fill$${pattern}`,
        `fish_mask$area_fill$${pattern}`,
      ])
    } else {
      showLayers([
        `fish_mask$pattern_fill$${pattern}`,
        `fish_mask$area_fill$${pattern}`,
      ])
    }
  },
  { immediate: true }
)

watch(
  mapViewerReady,
  (isReady) => {
    if (!isReady) {
      return
    }
    const mapManager = getMapManager()
    const mapViewer = mapManager?.viewer

    if (!mapViewer?.map) {
      return
    }

    // Ensure a default basemap is set for new users with no saved preference.
    // Must run after map-x-ready so setBasemap fires while Mapbox GL is fully operational.
    ensureDefaultBasemap()

    xWeather.initializeController(mapViewer.map)
  },
  { immediate: true }
)

// Turn wind layer on if last session had layer enabled, once xWeather is ready
watch(
  xWeather.isLoaded,
  (isLoaded) => {
    if (isLoaded && windLayerEnabled.value && hasWindAccess.value) {
      setWindLayer(true, xWeather)
    }
  },
  { immediate: true }
)

// Turn off wind layer when access to the gate is lost
watch(hasWindAccess, (accessEnabled) => {
  if (!accessEnabled) {
    setWindLayer(false, xWeather)
    saveItem('wind-forecast-enabled', false)
  }
})

///// lifecycle /////
onBeforeMount(() => {
  initializeXOfflineMapsWhenEnabled()
})

onMounted(async () => {
  awaitMap()
  // Set cypress dom flag
  markupsLoaded.then(() => (areMarkupsLoaded.value = true))

  // Make sure split is ready before we do anything else
  await split.splitClientsReady()
  initialPromises()

  // Initialize components of the app
  // No Android banner until Android app available
  if (browser.isiPhone() || browser.isiPad()) {
    new SmartBanner()
  }
  new Account()
  checkAndSetFilterPreferences()
  showFilterButton.value = !route?.path?.includes('waterbodies/filter')
  // Kick off all independent async actions simultaneously
  await Promise.all([
    waypointImageService.init(),
    markupService.initialRequestBbox(),
    markupService.initialRequestAll(),
    contentCollectionService.initialRequestCollections(),
    setCollectionsData(),
  ])
  highlightFavoritesOnMap({
    source: 'water-special',
    sourceLayer: 'lake:location',
    getFavoritesFn: getAllFavoritedWaterbodies,
  })
  fireSessionStart()
})

onUnmounted(() => {
  clearInterval(intervalId)
})
</script>
<style lang="scss" scoped>
@use '@/styles/system/_breakpoints.scss';
@use '@/styles/system/_animation.scss';
@use '@/styles/_layer-map.scss';

main {
  display: flex;
  width: 100%;
  height: 100%;
}

.map-page {
  height: 100%;
  display: flex;
  overflow: hidden;

  &.mobile-testbed main {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

.side-panel.panel-view {
  position: absolute;
  left: 100px;
  width: 350px;
  padding-top: 18px;
}

.controls-outside-right {
  position: absolute;
  right: 20px;
  bottom: 33px;
  top: 85px;
  &--align-top {
    top: 20px;
    @include breakpoints.mobile {
      top: 85px;
    }
  }

  @include breakpoints.app-prototyping {
    right: var(--mobile-controls-offset);
    bottom: calc(var(--mobile-navbar-height) + var(--mobile-controls-offset));
  }
}

.controls-inner-right {
  position: absolute;
  right: 80px;
  bottom: 33px;
  top: 85px;
  &--align-top {
    top: 20px;
  }
  @include breakpoints.laptop-height {
    top: 70px;
  }
}

.controls-top {
  position: absolute;
  top: 20px;
  left: 120px;
  display: flex;
  margin: 0 -10px;
  transition: left animation.$timing_250 animation.$ease_standard;
  @include breakpoints.mobile {
    left: 55px;
    transition: left 250ms ease;
    &.mobile-menu-open {
      left: 95px;
    }

    :deep(.desktop-search-bar) {
      width: 100%;
    }
  }
  & > .map-control {
    margin: 0 10px;
    pointer-events: all;
  }

  &.with-expanded-card {
    left: 870px;
    right: 85px;
  }
}

.quick-filters-container {
  position: absolute;
  top: 20px;
  left: 520px;
  display: flex;
  margin: 0 -10px;

  @include breakpoints.mobile {
    top: 80px;
    left: 0;
    right: 0;
    margin-inline: auto;
    width: fit-content;
  }
}
.controls-bottom-right {
  position: absolute;
  bottom: 0;
  right: 0;
  font-size: 0;
}

.controls-bottom-center {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  max-width: 465px;
}

.controls-bottom-left {
  position: absolute;
  bottom: 0;
  left: 100px;
  font-size: 0;
  @include breakpoints.mobile {
    left: 0px;
  }
}

.controls-column-center {
  display: flex;
  flex-direction: column;
  pointer-events: none;
  margin: auto;
  align-items: center;

  & > * {
    pointer-events: all;
    margin: 7.5px 0;
  }

  @include breakpoints.mobile {
    align-items: flex-start;
    left: var(--ys-spacing-200);
  }
}

.controls-column-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  pointer-events: none;
  margin: -7.5px 0;
  &.controls-inner-right {
    flex-direction: row;
    gap: var(--ys-spacing-200);
  }
  & > * {
    pointer-events: all;
    margin: 7.5px 0;
  }
}

.controls-spacer {
  flex-grow: 1;
  margin: 0;
}

#cursor-location {
  display: inline-block;
}

.x-loading-bar {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  z-index: 3;
}

:deep(#map) {
  width: 100%;
  height: 100%;
}

.info-card.format-html {
  :deep(.content ul) {
    margin: 0 0 0.75em;
  }
}

.nav-scrim {
  display: none;
  @include breakpoints.mobile() {
    display: inline;
    position: absolute;
    z-index: layer-map.$z-index-level-side-nav;
    top: 0;
    left: 0;
    width: 100%;
    height: 80px;
    background: -webkit-gradient(
      linear,
      left bottom,
      left top,
      color-stop(1, rgba(0, 0, 0, 1)),
      color-stop(0, rgba(0, 0, 0, 0))
    );

    background: -moz-linear-gradient(
      center bottom,
      color-stop(1, rgba(0, 0, 0, 1)),
      color-stop(0, rgba(0, 0, 0, 0))
    );
  }
}
</style>
